import { PurchaserInfo, PurchasesPackage } from '@ionic-native/purchases';
import Vue from 'vue';

import { SnowplowFreemiumContext } from '@/helpers/tracking/providers';
import { getVm } from '@/helpers/vm-helper';

import { Response } from '@/interfaces/revenueCat';

import revenueCat from '@/services/revenueCatAPI';
import { ReadyType } from '@/stores/ready.store';
import { Store } from 'vuex';

const iapCountries = [
	'AT',
	'AU',
	'BE',
	'BG',
	'CA',
	'CH',
	'CZ',
	'DE',
	'DK',
	'EE',
	'ES',
	'GB',
	'GR',
	'FI',
	'FR',
	'HR',
	'HU',
	'IE',
	'IT',
	'LV',
	'LT',
	'NZ',
	'NL',
	'PL',
	'PT',
	'RO',
	'SK',
	'SE',
	'US',
	'ZA',
];

export function isPremiumUser(): boolean {
	return getVm()?.$store.getters['user/isPremium'];
}

export function isSupportedPurchaseCountry(): boolean {
	const appCountry = getVm()?.$store.getters['language/country'];
	const isSupportedPurchaseCountry = iapCountries.includes(appCountry);
	return isSupportedPurchaseCountry;
}

export function showFreemium(): boolean {
	// show for premium-enabled users, no matter which country is set and for all platforms
	// show for supported countries on mobile (individual features are responsible for locked/unlocked state)
	// hide in all other cases
	return isSupportedPurchaseCountry() || isPremiumUser();
}

const IapHelper = {
	getPurchases: async () => (await import('@ionic-native/purchases')).Purchases,
	async updatePremiumStatus(store?: Store<any>): Promise<void> {
		const _store = store || getVm()?.$store;
		let newPremiumStatus = false;

		// we need consent before we load more 3rd party libs
		// ONLY FOR WEBAPP FOR NOW
		if (!global.VUE_APP_TESTS_E2E) {
			await Vue.$jw.ready?.waitFor(ReadyType.CONSENT_PARTNERS_ANSWERED);
		}

		const purchaserInfo = await this.getOrCreateSubscriber();

		if (purchaserInfo) {
			if (!('subscriber' in purchaserInfo)) {
				newPremiumStatus = purchaserInfo.activeSubscriptions.length > 0;
			} else {
				const expireDate = purchaserInfo && purchaserInfo?.subscriber?.entitlements['Pro']?.expires_date;
				if (expireDate) {
					newPremiumStatus = new Date(expireDate).getTime() > Date.now();
				}
			}
		}
		//@ts-ignore
		const subscriptionId = newPremiumStatus
			? (purchaserInfo as Response)?.subscriber?.entitlements['Pro']?.product_identifier
			: null;
		await _store.dispatch('user/setPremiumStatus', newPremiumStatus);
		// Start COOKIE_BANNER_EXPERIMENT
		await _store.dispatch('user/setSubscriptionInfo', subscriptionId);
	},

	async getOrCreateSubscriber(): Promise<PurchaserInfo | Response | null> {
		try {
			const newJwId = getVm().$store.getters['user/jwLoginId'];
			if (newJwId == null) return null;

			const response = await revenueCat.get<Response>(newJwId);
			return response.data;
		} catch {
			return null;
		}
	},

	async getSubscriptionPriceString(): Promise<string> {
		return new Promise(async (resolve, reject) => {
			try {
				const offerings = await (await this.getPurchases()).getOfferings();
				const priceString = offerings.current?.monthly?.product.price_string;
				if (priceString) {
					resolve(priceString);
				} else {
					reject('no price string returned');
				}
			} catch (error) {
				reject(error);
			}
		});
	},

	async makePurchase(): Promise<boolean> {
		return new Promise(async resolve => {
			let isPremium = false;
			try {
				const offerings = await (await this.getPurchases()).getOfferings();
				// @ts-ignore
				if (offerings.current !== null && offerings.current.availablePackages.length !== 0) {
					const offer = offerings.current.monthly;

					const { purchaserInfo } = await (
						await this.getPurchases()
					).purchasePackage(offer as PurchasesPackage);
					if (purchaserInfo && typeof purchaserInfo.entitlements.active['Pro'] !== 'undefined') {
						isPremium = true;
					}
				}
			} catch (e) {}
			await getVm().$store.dispatch('user/setPremiumStatus', isPremium);
			resolve(isPremium);
		});
	},
	async restorePurchases(): Promise<boolean> {
		return new Promise(async resolve => {
			let isPremium = false;
			try {
				const restoredPurchaserInfo = await (await this.getPurchases()).restoreTransactions();
				// ... check restored purchaserInfo to see if entitlement is now active
				if (restoredPurchaserInfo && typeof restoredPurchaserInfo.entitlements.active['Pro'] !== 'undefined') {
					isPremium = true;
				}
			} catch (e) {}
			await getVm().$store.dispatch('user/setPremiumStatus', isPremium);
			resolve(isPremium);
		});
	},
};

export const FreemiumHelper = {
	getFreemiumContext() {
		const isHideSeenDislikedTitlesEnabled =
			getVm()?.$store.getters['user/isHideSeenDislikedTitlesEnabled'] ?? false;

		return new SnowplowFreemiumContext(isHideSeenDislikedTitlesEnabled);
	},
};

export default IapHelper;
